import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getCompanyMetricsHistory } from '../../http/CompaniesApi';
import MetricsRangeSelectorComponent from '../portfolios/tabs/analytics/MetricsRangeSelectorComponent';
import {
  DEFAULT_HISTORY_RANGE,
  METRICS,
  formatTimestamp,
  hasValues,
  toAverageOrNull,
  toChartPoints,
} from './metricsHistory';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
};

const COLORS = {
  SURFACE: '#ffffff',
  BORDER: '#e0e0e0',
  TEXT_SECONDARY: '#5f6368',
  LINE: '#6366f1',
  AVERAGE: '#9e9e9e',
  GRID: '#ccc',
};

const CHART_HEIGHT = 220;
// Phones lose most of their width to nested padding, so it is trimmed there.
const CARD_PADDING = isMobile ? SPACING.XS : SPACING.SM;
const Y_AXIS_WIDTH = isMobile ? 45 : 60;
const DEFAULT_ERROR_MESSAGE = 'Could not load the metrics history.';

const cacheKey = (companyId, range) => `${companyId}:${range}`;

const errorMessage = (err) =>
  get(err, 'response.status') === 404
    ? 'This company no longer exists.'
    : get(err, 'response.data.message', DEFAULT_ERROR_MESSAGE);

function MetricCard({ title, unit, children }) {
  return (
    <Grid item xs={12} md={6} lg={4}>
      <Paper
        elevation={0}
        sx={{
          p: CARD_PADDING,
          height: '100%',
          border: `1px solid ${COLORS.BORDER}`,
          borderRadius: '8px',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
          {unit && (
            <Typography
              component="span"
              variant="caption"
              color={COLORS.TEXT_SECONDARY}
              sx={{ ml: SPACING.XS }}
            >
              ({unit})
            </Typography>
          )}
        </Typography>
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>
      </Paper>
    </Grid>
  );
}

function MetricChart({ metric, points, currency, average, range }) {
  if (!hasValues(points, metric.key)) {
    return (
      <Typography variant="body2" color={COLORS.TEXT_SECONDARY}>
        No data for this metric
      </Typography>
    );
  }

  const formatValue = (value) => metric.format(value, currency);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={points} margin={{ top: 8, right: 8, left: 0 }}>
        <CartesianGrid stroke={COLORS.GRID} strokeDasharray="5 5" />
        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(ts) => formatTimestamp(ts, range)}
          fontSize={11}
          minTickGap={24}
        />
        <YAxis
          domain={['auto', 'auto']}
          // The card title already names the currency; keep the ticks narrow.
          tickFormatter={(value) => metric.format(value, null)}
          fontSize={11}
          width={Y_AXIS_WIDTH}
        />
        <Tooltip
          labelFormatter={(ts) => formatTimestamp(ts, range)}
          formatter={(value) => [formatValue(value), metric.title]}
        />
        {average !== null && (
          <ReferenceLine
            y={average}
            stroke={COLORS.AVERAGE}
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={{
              value: `avg ${formatValue(average)}`,
              position: 'insideTopRight',
              fontSize: 11,
              fill: COLORS.TEXT_SECONDARY,
            }}
          />
        )}
        {/* connectNulls stays off: a null is a missing day and must show as a gap. */}
        <Line
          type="monotone"
          dataKey={metric.key}
          stroke={COLORS.LINE}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * `cache` is owned by the parent so responses survive closing and reopening
 * the detail; it is keyed by (companyId, range).
 */
export default function CompanyMetricsHistoryComponent({
  company,
  cache,
  onClose,
}) {
  const [range, setRange] = useState(DEFAULT_HISTORY_RANGE);
  const [retries, setRetries] = useState(0);
  const [state, setState] = useState({
    loading: true,
    error: null,
    history: null,
  });

  useEffect(() => {
    const key = cacheKey(company.id, range);
    if (cache.has(key)) {
      setState({ loading: false, error: null, history: cache.get(key) });
      return undefined;
    }

    // Ignore late responses after the company or range has changed.
    let cancelled = false;
    setState({ loading: true, error: null, history: null });
    getCompanyMetricsHistory(company.id, range)
      .then(({ data }) => {
        cache.set(key, data);
        if (!cancelled) {
          setState({ loading: false, error: null, history: data });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ loading: false, error: errorMessage(err), history: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [company.id, range, retries, cache]);

  const points = useMemo(
    () => toChartPoints(state.history?.points),
    [state.history],
  );

  const renderBody = () => {
    if (state.loading) {
      return (
        <Grid container spacing={SPACING.SM} data-testid="metrics-loading">
          {METRICS.map((metric) => (
            <MetricCard
              key={metric.key}
              title={metric.title}
              unit={metric.unit}
            >
              <Skeleton variant="rounded" width="100%" height="100%" />
            </MetricCard>
          ))}
        </Grid>
      );
    }

    if (state.error) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setRetries((n) => n + 1)}
            >
              Retry
            </Button>
          }
        >
          {state.error}
        </Alert>
      );
    }

    if (points.length === 0) {
      return (
        <Typography variant="body2" color={COLORS.TEXT_SECONDARY}>
          No history yet for this range
        </Typography>
      );
    }

    return (
      <Grid container spacing={SPACING.SM}>
        {METRICS.map((metric) => (
          <MetricCard
            key={metric.key}
            title={metric.title}
            unit={metric.key === 'price' ? state.history.currency : metric.unit}
          >
            <MetricChart
              metric={metric}
              points={points}
              currency={state.history.currency}
              average={
                metric.averageKey
                  ? toAverageOrNull(company.metrics?.[metric.averageKey])
                  : null
              }
              range={range}
            />
          </MetricCard>
        ))}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        p: CARD_PADDING,
        backgroundColor: COLORS.SURFACE,
        borderRadius: '8px',
        border: `1px solid ${COLORS.BORDER}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: SPACING.SM,
          mb: SPACING.MD,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {company.symbol} · {company.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.XS }}>
          <MetricsRangeSelectorComponent
            metricsRange={range}
            setMetricsRange={setRange}
          />
          <IconButton onClick={onClose} title="Close metrics">
            <Close />
          </IconButton>
        </Box>
      </Box>
      {renderBody()}
    </Box>
  );
}
